import * as React from "react";
import type { WatchNoteChainLink, WatchNoteFact, WatchNoteStatusChange } from "@hp/api-client";
import { formatDate } from "../../lib/format";
import { HeldBadgeButton, SourceChip, SourceChips } from "./SourceChips";
import { chainLinkCite, groupFacts, statusHistoryOldestFirst, statusTone, type StatusTone } from "./sourceHelpers";

/** A small uppercase heading, the same weight as the card's field labels. */
function SectionHeading({ id, children }: { id?: string; children: React.ReactNode }) {
  return (
    <h5 id={id} className="text-[0.6875rem] font-medium uppercase tracking-wide text-zinc-400">
      {children}
    </h5>
  );
}

/**
 * The facts under headings (what it holds, our side, value, action), each fact
 * followed by the chips of the pages it was read from, so a lawyer can check
 * every statement against the order itself.
 */
export function FactGroups({ facts, noteId }: { facts: WatchNoteFact[]; noteId: string }) {
  const groups = groupFacts(facts);
  return (
    <div className="mt-2 space-y-2">
      {groups.map((group) => {
        const headingId = `wn-${noteId}-${group.key}`;
        return (
          <section key={group.key} aria-labelledby={headingId}>
            <SectionHeading id={headingId}>{group.heading}</SectionHeading>
            <ul className="mt-0.5 space-y-1 text-sm text-zinc-800">
              {group.facts.map((fact, i) => (
                <li key={i} className="leading-snug">
                  {fact.text}
                  <SourceChips cites={fact.cites} />
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

/** The date of a timeline entry, or a dash: an undated order still has its place in the chain. */
function TimelineDate({ date }: { date: string | null }) {
  return (
    <span className="w-[5.5rem] flex-none text-xs tabular-nums text-zinc-500">{date ? formatDate(date) : "undated"}</span>
  );
}

/** The dot and the line down to the next entry. The last entry has no line, so the timeline visibly ends. */
function Rail({ last, tone = "neutral" }: { last: boolean; tone?: StatusTone }) {
  const dot: Record<StatusTone, string> = {
    neutral: "bg-zinc-300",
    warning: "bg-amber-400",
    danger: "bg-red-500",
    success: "bg-emerald-500",
  };
  return (
    <span className="relative flex w-3 flex-none justify-center self-stretch" aria-hidden>
      <span className={`mt-1.5 h-2 w-2 rounded-full ${dot[tone]}`} />
      {last ? null : <span className="absolute bottom-[-0.5rem] top-4 w-px bg-zinc-200" />}
    </span>
  );
}

/**
 * The chain of orders as a vertical timeline, oldest first, the way the
 * history happened. Each order has its outcome and either a HELD badge (opens
 * the order at the cited page) or TO FETCH, so a gap in the record is obvious.
 */
export function ChainTimeline({ chain }: { chain: WatchNoteChainLink[] }) {
  return (
    <ol className="space-y-2" aria-label="Chain of orders, oldest first">
      {chain.map((link, i) => {
        const cite = chainLinkCite(link);
        const showForum = link.forum && !link.ref.toUpperCase().startsWith(link.forum.toUpperCase());
        return (
          <li key={`${link.ref}-${i}`} className="flex gap-2 text-xs text-zinc-700">
            <TimelineDate date={link.date} />
            <Rail last={i === chain.length - 1} />
            <div className="min-w-0 flex-1">
              <span className="font-medium text-zinc-900">
                {showForum ? `${link.forum} ` : ""}
                {link.ref}
              </span>
              {link.outcome ? <span className="text-zinc-600"> · {link.outcome}</span> : null}{" "}
              {cite ? (
                <HeldBadgeButton cite={cite} />
              ) : link.held ? (
                // Held, but an older server did not say which document it is: nothing to open.
                <span className="rounded bg-zinc-100 px-1 py-px text-[0.625rem] font-medium uppercase tracking-wide text-zinc-600">held</span>
              ) : (
                <span className="rounded bg-amber-50 px-1 py-px text-[0.625rem] font-medium uppercase tracking-wide text-amber-700 ring-1 ring-inset ring-amber-200">
                  to fetch
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

const STATUS_CHIP: Record<StatusTone, string> = {
  neutral: "bg-zinc-100 text-zinc-700",
  warning: "bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200",
  danger: "bg-red-50 text-red-700 ring-1 ring-inset ring-red-200",
  success: "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200",
};

/**
 * "How its standing changed": each dated change in the precedent's standing
 * (admitted, stayed, set aside, final...) with the record it comes from.
 * Renders nothing when the standing never changed, so the card stays short.
 */
export function StatusTimeline({ history }: { history: WatchNoteStatusChange[] | undefined }) {
  const ordered = statusHistoryOldestFirst(history);
  if (!ordered.length) return null;
  return (
    <ol className="space-y-2" aria-label="How its standing changed, oldest first">
      {ordered.map((entry, i) => {
        const tone = statusTone(entry.status);
        return (
          <li key={`${entry.date ?? "undated"}-${i}`} className="flex gap-2 text-xs text-zinc-700" data-status-tone={tone}>
            <TimelineDate date={entry.date} />
            <Rail last={i === ordered.length - 1} tone={tone} />
            <div className="min-w-0 flex-1 leading-snug">
              <span className={`mr-1 rounded px-1 py-px text-[0.625rem] font-semibold uppercase tracking-wide ${STATUS_CHIP[tone]}`}>
                {entry.status}
              </span>
              {entry.text}
              {entry.cite ? (
                <span className="ml-1 inline-flex align-baseline">
                  <SourceChip cite={entry.cite} />
                </span>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
